import * as next from '../../../types/next'
import type { BlockUiSchema } from '../../../utils'
import { CONTROL_NAME } from '../../../utils'
import type * as current from '../../../utils/types/sharedTypes'
import type { UpBlockHandler } from '../types'
import { findBlockIdWithinStep, newId } from '../utils'

import { upCommonSettings } from './common'

export const up: UpBlockHandler<next.EntityFinderBlock> = (
  uischema,
  _,
  journey
) => {
  if (uischema.type !== CONTROL_NAME.ENTITY_LOOKUP_CONTROL) {
    throw new Error('Expected a valid Entity lookup control')
  }

  if (!journey) {
    throw new Error('Cannot migrate Entity lookup block without journey')
  }

  return {
    type: next.BLOCK_TYPE.ENTITY_FINDER,
    required: false, // can never be required
    settings: {
      ...upCommonSettings(uischema),
      ...upBlockSettings(uischema)
    },
    references: createReferences(uischema, journey)
  }
}

function upBlockSettings(uischema: BlockUiSchema) {
  const settings = {
    showIcon: uischema.options?.showIcon,
    multiSelect: Boolean(uischema.options?.multiSelectAllowed),
    entity: uischema.options?.slug,
    attributes: uischema.options?.tableAttributes,
    titleAttribute: uischema.options?.titleAttribute
  }

  if (uischema.options?.relatedLookupBlock) {
    // mode: Other block
    return {
      ...settings,
      mode: next.EntitySelectionMode.OTHER_BLOCK,
      relation: uischema.options?.relatedContextAttribute,
      relationSlug: uischema.options?.relatedContextAttributeSlug
    } as next.OtherBlockBasedEntityFinderBlock['settings']
  }

  if (uischema.options?.relatedContextEntity) {
    // mode: Context param
    return {
      ...settings,
      mode: next.EntitySelectionMode.CONTEXT,
      relation: uischema.options?.relatedContextAttribute,
      relationSlug: uischema.options?.relatedContextAttributeSlug
    } as next.ContextBasedEntityFinderBlock['settings']
  }

  // if (uischema.options?.slug && uischema.options?.tableAttributes)
  // We use entity list as the default mode as well
  return {
    ...settings,
    mode: next.EntitySelectionMode.ENTITY_LIST
  } as next.EntityListBasedEntityFinderBlock['settings']
}

function createReferences(uischema: BlockUiSchema, journey: current.Journey) {
  if (uischema.options?.relatedLookupBlock) {
    // other block mode
    return createReferenceFromLookupBlock(
      uischema.options?.relatedLookupBlock,
      journey
    )
  }

  if (uischema.options?.relatedContextEntity) {
    return createContextReference(uischema.options?.relatedContextEntity)
  }

  // Entity list mode does not require references
  return {}
}

function createReferenceFromLookupBlock(
  relatedLookupBlock: string,
  journey: current.Journey
): Record<next.ReferenceId, next.EntityFinderBlockReference> {
  const [stepIdx, blockName] = relatedLookupBlock.split('/')
  const blockId = findBlockIdWithinStep(journey, Number(stepIdx), blockName)
  const referenceId = newId<next.ReferenceId>()

  return {
    [referenceId]: {
      meaning: 'source',
      referenceType: next.REFERENCED_TYPE.BLOCK,
      referenceId: blockId,
      id: referenceId
    } as next.EntityFinderBlockReference
  }
}

function createContextReference(
  contextParameterKey: string
): Record<next.ReferenceId, next.EntityFinderContextReference> {
  const referenceId = newId<next.ReferenceId>()

  return {
    [referenceId]: {
      meaning: 'source',
      referenceType: next.REFERENCED_TYPE.CONTEXT_PARAMETER,
      referenceId: contextParameterKey,
      id: referenceId
    } as next.EntityFinderContextReference
  }
}
